import React, { useState, useEffect } from "react";
import {
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  LinearProgress,
  Avatar,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  rootPaper: {
    padding: theme.spacing(2.5),
    borderRadius: 12,
    border: "1px solid #e2e8f0",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
    backgroundColor: "#ffffff",
    marginBottom: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
    gap: 8,
  },
  title: {
    fontWeight: "bold",
    fontSize: "1.1rem",
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#0f172a",
  },
  kpiCard: {
    padding: theme.spacing(2),
    borderRadius: 10,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
    border: "1px solid #f1f5f9",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
    },
  },
  kpiLabel: {
    fontSize: "0.78rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  kpiValue: {
    fontSize: "1.85rem",
    fontWeight: 800,
    marginTop: 4,
    marginBottom: 2,
    lineHeight: 1.1,
  },
  kpiSubtitle: {
    fontSize: "0.75rem",
    color: "#64748b",
  },
  funnelSection: {
    marginTop: theme.spacing(2.5),
    paddingTop: theme.spacing(2),
    borderTop: "1px solid #f1f5f9",
  },
  stageBar: {
    height: 10,
    borderRadius: 5,
    backgroundColor: "#f1f5f9",
  },
  channelItem: {
    padding: "10px 14px",
    backgroundColor: "#f8fafc",
    borderRadius: 8,
    border: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  attendantCard: {
    padding: "12px 14px",
    borderRadius: 10,
    border: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    transition: "all 0.2s ease",
  },
}));

const FunnelConversionDashboard = ({ whatsappId = "", startDate = "", endDate = "" }) => {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    api
      .get("/contacts/funnel-metrics", {
        params: {
          whatsappId: whatsappId || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        },
      })
      .then((res) => {
        if (isMounted) {
          setData(res.data);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("Erro ao carregar métricas de conversão:", err);
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [whatsappId, startDate, endDate]);

  if (loading) {
    return (
      <Paper className={classes.rootPaper}>
        <Box display="flex" justifyContent="center" alignItems="center" py={4} style={{ gap: 8 }}>
          <CircularProgress size={24} style={{ color: "#0ea5e9" }} />
          <Typography variant="body2" style={{ color: "#64748b" }}>
            Carregando indicadores de conversão e agendamentos...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (!data) return null;

  const { totalContacts, scheduled, conversionRate, funnel, channels, attendants = [] } = data;
  const total = totalContacts || 1;

  const pctInterested = Math.round(((funnel.interested || 0) / total) * 100);
  const pctQualified = Math.round(((funnel.qualified || 0) / total) * 100);
  const pctScheduled = Math.round(((funnel.scheduled || 0) / total) * 100);
  const pctLost = Math.round(((funnel.lost || 0) / total) * 100);

  return (
    <Paper className={classes.rootPaper}>
      {/* HEADER */}
      <div className={classes.header}>
        <div className={classes.title}>
          <TrendingUpIcon style={{ color: "#0284c7" }} />
          <span>🎯 Funil de Atendimento & Efetividade de Agendamentos</span>
        </div>
        <Typography variant="caption" style={{ color: "#94a3b8", fontWeight: 500 }}>
          {whatsappId ? "Métricas da conexão selecionada" : "Visão consolidada (todas as conexões)"}
        </Typography>
      </div>

      {/* KPI TOP CARDS */}
      <Grid container spacing={2}>
        {/* Total Pacientes */}
        <Grid item xs={12} sm={6} md={3}>
          <div className={classes.kpiCard} style={{ backgroundColor: "#f0f9ff", borderColor: "#bae6fd" }}>
            <div className={classes.kpiLabel} style={{ color: "#0369a1" }}>
              <PeopleAltIcon fontSize="small" /> Pacientes Únicos
            </div>
            <div className={classes.kpiValue} style={{ color: "#0c4a6e" }}>
              {totalContacts.toLocaleString("pt-BR")}
            </div>
            <div className={classes.kpiSubtitle}>
              Pessoas atendidas no sistema
            </div>
          </div>
        </Grid>

        {/* Agendamentos */}
        <Grid item xs={12} sm={6} md={3}>
          <div className={classes.kpiCard} style={{ backgroundColor: "#f0fdf4", borderColor: "#bbf7d0" }}>
            <div className={classes.kpiLabel} style={{ color: "#15803d" }}>
              <EventAvailableIcon fontSize="small" /> Agendamentos
            </div>
            <div className={classes.kpiValue} style={{ color: "#14532d" }}>
              {scheduled.toLocaleString("pt-BR")}
            </div>
            <div className={classes.kpiSubtitle}>
              Fechamentos com data marcada
            </div>
          </div>
        </Grid>

        {/* Efetividade / Taxa */}
        <Grid item xs={12} sm={6} md={3}>
          <div className={classes.kpiCard} style={{ backgroundColor: "#faf5ff", borderColor: "#e9d5ff" }}>
            <div className={classes.kpiLabel} style={{ color: "#7e22ce" }}>
              <TrendingUpIcon fontSize="small" /> Taxa de Efetividade
            </div>
            <div className={classes.kpiValue} style={{ color: "#581c87" }}>
              {conversionRate}%
            </div>
            <div className={classes.kpiSubtitle}>
              1 a cada {conversionRate > 0 ? (100 / conversionRate).toFixed(1) : 0} contatos agenda
            </div>
          </div>
        </Grid>

        {/* Em Qualificação (Oportunidades) */}
        <Grid item xs={12} sm={6} md={3}>
          <div className={classes.kpiCard} style={{ backgroundColor: "#eff6ff", borderColor: "#bfdbfe" }}>
            <div className={classes.kpiLabel} style={{ color: "#1d4ed8" }}>
              <CheckCircleOutlineIcon fontSize="small" /> Oportunidades Ativas
            </div>
            <div className={classes.kpiValue} style={{ color: "#1e3a8a" }}>
              {funnel.qualified.toLocaleString("pt-BR")}
            </div>
            <div className={classes.kpiSubtitle}>
              Pacientes qualificados para resgate
            </div>
          </div>
        </Grid>
      </Grid>

      {/* SEÇÃO DO FUNIL, RANKING DE ATENDENTES & CANAIS */}
      <div className={classes.funnelSection}>
        <Grid container spacing={3}>
          {/* COLUNA 1: ETAPAS DO FUNIL */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#334155", marginBottom: 12 }}>
              Etapas do Funil de Atendimento
            </Typography>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {/* Interessados */}
              <div>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="caption" style={{ fontWeight: 600, color: "#b45309" }}>
                    🟡 Sondagem & Interesse Inicial
                  </Typography>
                  <Typography variant="caption" style={{ fontWeight: 700, color: "#475569" }}>
                    {funnel.interested} ({pctInterested}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, pctInterested)}
                  className={classes.stageBar}
                  style={{ backgroundColor: "#fef3c7" }}
                />
              </div>

              {/* Qualificados */}
              <div>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="caption" style={{ fontWeight: 600, color: "#1d4ed8" }}>
                    🔵 Qualificados (Tirou Dúvidas)
                  </Typography>
                  <Typography variant="caption" style={{ fontWeight: 700, color: "#475569" }}>
                    {funnel.qualified} ({pctQualified}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, pctQualified)}
                  className={classes.stageBar}
                  style={{ backgroundColor: "#dbeafe" }}
                />
              </div>

              {/* Agendados */}
              <div>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="caption" style={{ fontWeight: 600, color: "#15803d" }}>
                    🟢 Agendados & Confirmados
                  </Typography>
                  <Typography variant="caption" style={{ fontWeight: 700, color: "#475569" }}>
                    {funnel.scheduled} ({pctScheduled}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, pctScheduled)}
                  className={classes.stageBar}
                  style={{ backgroundColor: "#dcfce7" }}
                />
              </div>

              {/* Não Agendou / Cancelou */}
              <div>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="caption" style={{ fontWeight: 600, color: "#b91c1c" }}>
                    🔴 Desistência / Não Agendou
                  </Typography>
                  <Typography variant="caption" style={{ fontWeight: 700, color: "#475569" }}>
                    {funnel.lost} ({pctLost}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, pctLost)}
                  className={classes.stageBar}
                  style={{ backgroundColor: "#fee2e2" }}
                />
              </div>
            </div>
          </Grid>

          {/* COLUNA 2: RANKING DE ATENDENTES (QUEM AGENDOU MAIS) */}
          <Grid item xs={12} md={4}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#334155", display: "flex", alignItems: "center", gap: 6 }}>
                <EmojiEventsIcon style={{ color: "#eab308", fontSize: 20 }} /> Ranking de Agendamentos
              </Typography>
              <Typography variant="caption" style={{ color: "#94a3b8" }}>
                Atendentes
              </Typography>
            </div>

            {attendants.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {attendants.map((att, idx) => {
                  const isFirst = idx === 0;
                  const medal = idx === 0 ? "🥇" : idx === 1 ? "🥈" : "🥉";
                  const bgColor = isFirst ? "#fffbeb" : "#f8fafc";
                  const borderColor = isFirst ? "#fde68a" : "#e2e8f0";

                  return (
                    <div
                      key={att.id || idx}
                      className={classes.attendantCard}
                      style={{ backgroundColor: bgColor, borderColor: borderColor }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: "1.2rem" }}>{medal}</span>
                        <Avatar
                          style={{
                            width: 34,
                            height: 34,
                            fontSize: "0.85rem",
                            fontWeight: "bold",
                            backgroundColor: isFirst ? "#f59e0b" : "#64748b",
                            color: "#fff",
                          }}
                        >
                          {att.name?.charAt(0).toUpperCase() || "A"}
                        </Avatar>
                        <div>
                          <Typography variant="body2" style={{ fontWeight: 700, color: "#1e293b" }}>
                            {att.name}
                          </Typography>
                          <Typography variant="caption" style={{ color: "#64748b" }}>
                            {att.total} atendimentos no período
                          </Typography>
                        </div>
                      </div>

                      <div style={{ textAlign: "right" }}>
                        <Typography variant="body2" style={{ fontWeight: 800, color: "#15803d", fontSize: "1rem" }}>
                          📅 {att.scheduled}
                        </Typography>
                        <Typography variant="caption" style={{ color: "#0369a1", fontWeight: 700, fontSize: "0.75rem" }}>
                          {att.rate}% conv.
                        </Typography>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Typography variant="caption" style={{ color: "#94a3b8" }}>
                Sem atendimentos registrados no filtro atual.
              </Typography>
            )}
          </Grid>

          {/* COLUNA 3: EFETIVIDADE POR CANAL DE ORIGEM */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#334155", marginBottom: 12 }}>
              Efetividade por Canal de Entrada
            </Typography>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {channels.map((ch, idx) => (
                <div key={idx} className={classes.channelItem}>
                  <div>
                    <Typography variant="body2" style={{ fontWeight: 700, color: "#1e293b", fontSize: "0.85rem" }}>
                      {ch.name}
                    </Typography>
                    <Typography variant="caption" style={{ color: "#64748b" }}>
                      {ch.leads} contatos • {ch.scheduled} agendados
                    </Typography>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <Typography
                      variant="body2"
                      style={{
                        fontWeight: 800,
                        color: ch.rate >= 10 ? "#16a34a" : ch.rate > 0 ? "#0284c7" : "#94a3b8",
                        fontSize: "0.95rem",
                      }}
                    >
                      {ch.rate}%
                    </Typography>
                    <Typography variant="caption" style={{ color: "#94a3b8", fontSize: "0.7rem" }}>
                      conversão
                    </Typography>
                  </div>
                </div>
              ))}
            </div>
          </Grid>
        </Grid>
      </div>
    </Paper>
  );
};

export default FunnelConversionDashboard;
